import React, { useEffect, useState } from 'react';
import { logout, scheduleExecution, setConnection } from 'auth/SystemAuth/context/actions';
import { useSystemAuthDispatch, useSystemAuthState } from 'auth/SystemAuth/context/context';
import { refreshToken } from 'auth/SystemAuth/context/systemToSystemAuth';
import { AxiosError } from 'axios';
import Unauthorized from 'components/Unauthorized';
import { isBefore, subMinutes } from 'date-fns';
import { useAppConfig } from 'hooks/queries';
import { useAppDispatch } from 'hooks/redux';
import { Outlet, useNavigate } from 'react-router-dom';
import { setAppConfig } from 'store/appConfigSlice';
import { saveElementValuesAsync } from 'store/rfiSlice';
import * as signalR from '@microsoft/signalr';
import useSessionTimeout from '../../hooks/useSessionTimeout';

// for testing date-fns in dev console
// import * as datefns from 'date-fns';
// @ts-ignore
// window.datefns = datefns;

let tokenExpires = localStorage.getItem("SystemTokenExpires") ?? null;

interface RequireSystemAuthProps {
  children?: JSX.Element;
}

export type SystemUser = {
  userId?: string;
  email?: string;
};

export const RequireSystemAuth = ({ children }: RequireSystemAuthProps) => {
  // Hooks
  const navigate = useNavigate();

  const authState = useSystemAuthState();
  const authDispatch = useSystemAuthDispatch();
  const dispatch = useAppDispatch();
  const {
    isLoading: appConfigIsLoading,
    data: appConfig,
    refetch,
  } = useAppConfig();

  const onLogout = () => {
    dispatch(saveElementValuesAsync()).finally(() => {
      logout(authDispatch);
      navigate("/loggedout");
    });
  };

  function handleRefreshToken() {
    let tokenExpires = localStorage.getItem("SystemTokenExpires");
    if (tokenExpires) {
      const tokenExpiresInt = parseInt(tokenExpires);
      if (isBefore(tokenExpiresInt, new Date())) {
        localStorage.removeItem("SystemTokenExpires");
        logout(authDispatch);
        return;
      }

      const timeoutDateTime = subMinutes(tokenExpiresInt, 5);
      //Refresh token 5 minutes before expiration
      scheduleExecution(timeoutDateTime, () => {
        refreshToken(authDispatch);
      });
    } else {
      logout(authDispatch);
    }
  }

  //TODO fix "A non-serializable value was detected in the state, in the path: `timeoutModal.getRemainingTime`. Value: () => 0"
  useSessionTimeout(1200000, 300000, onLogout);

  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const getToken = (): Promise<string> => {
    return new Promise((resolve, reject) => {
      authState.token ? resolve(authState.token) : resolve("");
    });
  };

  useEffect(() => {
    if (!authState.user && tokenExpires)
      localStorage.removeItem("SystemTokenExpires");
    if (authState.user && !authState.tokenExpires) handleRefreshToken();
  }, [tokenExpires]);

  useEffect(() => {
    if (appConfig) {
      dispatch(setAppConfig(appConfig));
    }
  }, []);

  useEffect(() => {
    const options: signalR.IHttpConnectionOptions = {
      // skipNegotiation: true,
      // transport: signalR.HttpTransportType.WebSockets,
      //headers: { "x-rfi-id": `${rfiId}` },
      accessTokenFactory: getToken,
    };
    const startConnection = async () => {
      setIsLoading(true);

      const localConnection = new signalR.HubConnectionBuilder()
        //.configureLogging(signalR.LogLevel.Debug)
        //.withAutomaticReconnect()
        .withUrl(`/rfiHub`, options)
        .build();

      if (localConnection) {
        setConnection(authDispatch, localConnection);

        await localConnection
          .start()
          .then(async (result: any) => {
            setIsConnected(true);

            //console.log(`${authState.user} Connected with id: ${localConnection?.connectionId}`);

            localConnection.on("DisconnectedSignal", (result: any) => {
              console.log(`${result ?? "Unknown"} disconnected`);
            });

            localConnection.on("ConnectedSignal", (result: any) => {
              //console.log(`${result}`);
            });

            localConnection.on("newMessage", (message: string) => {
              //console.log(`Message: ${message} `);
            });
          })
          .catch((error: Error | AxiosError) =>
            console.log(`localConnection failed: ${error}`)
          )
          .finally(() => setIsLoading(false));

        // TODO: BEARER TOKENS ARE NOT SENT THROUGH THE AUTH HEADER.
        //       It goes in the query string so either F to SignalR or we pass it securely through
        //       the query string.
      }
    };

    startConnection();
  }, [authState.token]);

  //if (authState.loading || isLoading) return <Loading />;

  if (authState.errorMessage) return <Unauthorized />;

  if (authState.token) {
    return children || <Outlet />;
  }

  return <Unauthorized />;
};
